// src/services/registrationService.js
import { db, auth } from '../config/firebase';
import { COLLECTIONS } from '../config/flavor';
import {
    collection,
    query,
    where,
    getDocs,
    getDoc,
    doc,
    addDoc,
    updateDoc,
    setDoc,
    deleteDoc
} from 'firebase/firestore';

// Collection names are flavor-aware (see /src/config/flavor.js).
//   - FLAVOR=prod    → 'registrations' + 'publicRegistrations'
//   - FLAVOR=preview → 'registrations_preview' + 'publicRegistrations_preview'
const REG_COL = COLLECTIONS.registrations;
const PUB_COL = COLLECTIONS.publicRegistrations;

// Check if a product is registered by its SKU
export const checkRegistration = async (sku) => {
    console.log(`Checking registration for SKU: ${sku}`);
    
    try {
        // If user is authenticated, first check private registrations for the most accurate data
        if (auth.currentUser) {
            console.log("User is authenticated, checking private registrations first");
            try {
                // Query MUST be constrained to the signed-in user's own docs —
                // the Firestore owner-read rule rejects collection queries that
                // could return other users' registrations, so an unconstrained
                // where('sku'==…) permission-fails wholesale. (2026-06-05 fix:
                // this silent failure let owners re-register their own board
                // and create duplicates on preview.)
                const registrationsRef = collection(db, REG_COL);
                const q = query(
                    registrationsRef,
                    where('userId', '==', auth.currentUser.uid),
                    where('sku', '==', sku)
                );
                const querySnapshot = await getDocs(q);
                
                if (!querySnapshot.empty) {
                    const docSnapshot = querySnapshot.docs[0];
                    const registration = {
                        ...docSnapshot.data(),
                        id: docSnapshot.id
                    };
                    
                    console.log("Found in private registrations:", registration);
                    return {
                        exists: true,
                        data: registration,
                        needsAuth: false
                    };
                }
                
                console.log("Not found in private registrations");
            } catch (error) {
                console.log("Error querying private registrations:", error);
                // Fall back to public registrations
            }
        }
        
        // Then check public registrations (which anyone can read)
        console.log("Checking publicRegistrations collection...");
        const publicRef = doc(db, PUB_COL,sku);
        const publicDoc = await getDoc(publicRef);
        
        if (publicDoc.exists()) {
            // Public registration exists (which means it's verified)
            // Force verified flag to true for public registrations
            const publicData = publicDoc.data();
            console.log("Found in public registrations:", publicData);
            
            return {
                exists: true,
                data: { 
                    ...publicData,
                    id: `public-${sku}`,
                    verified: true  // Always true for public registrations
                },
                needsAuth: false
            };
        }
        
        // Not found in either collection - no registration exists
        console.log("Registration not found in any collection");
        return {
            exists: false,
            data: null,
            needsAuth: false
        };
    } catch (error) {
        console.error("Error checking registration:", error);
        
        // If permission-denied, suggest authentication
        if (error.code === 'permission-denied') {
            return {
                exists: false,
                data: null,
                needsAuth: true,
                message: "Unable to check registration. Please sign in."
            };
        }
        
        throw error;
    }
};

// Register a new product
export const registerProduct = async (productData, user) => {
    if (!user) {
        throw new Error('User must be authenticated to register a product');
    }

    const registration = {
        ...productData,
        registeredUserName: user.displayName,
        registeredUserEmail: user.email,
        userId: user.uid,
        verified: false,  // Always false for new registrations
        registrationDate: new Date(),
        showName: true,
        showEmail: true,
        lastUpdated: new Date()
    };

    // Check if this product already exists in publicRegistrations
    // This is a safety check to prevent duplicate registrations
    const publicRef = doc(db, PUB_COL,productData.sku);
    const publicDoc = await getDoc(publicRef);

    if (publicDoc.exists()) {
        throw new Error('This product is already registered and verified.');
    }

    const docRef = await addDoc(collection(db, REG_COL), registration);
    return {
        ...registration,
        id: docRef.id
    };
};

// Register a new product with verification data
export const registerProductWithVerification = async (productData, user, verificationData) => {
    if (!user) {
        throw new Error('User must be authenticated to register a product');
    }

    const {
        purchaseLocation,
        shopifyMatch = false,
        verificationMethod,
        orderNumber = null
    } = verificationData;

    // Determine if this should auto-verify
    const autoVerified = verificationMethod === 'shopify-auto' || verificationMethod === 'retailer-auto';

    const registration = {
        ...productData,
        registeredUserName: productData.displayName || user.displayName,
        registeredUserEmail: user.email,
        userId: user.uid,
        verified: false,  // Will be updated by Cloud Function if autoVerified
        registrationDate: new Date(),
        showName: productData.showName !== undefined ? productData.showName : true,
        // Legacy field — kept in sync with findability.showEmail below.
        showEmail: productData.showEmail !== undefined ? productData.showEmail : true,
        // Findability defaults (Nigel, 2026-06-05): first name + EMAIL on by
        // default — a registered board should be findable AND contactable out
        // of the box; owners can switch email off any time. City/phone stay
        // opt-in. The regeneratePublicRegistration trigger derives the public doc.
        findability: {
            enabled: true,
            showFirstName: true,
            showCity: false,
            showOwnerMessage: false, // flips on when an ownerMessage is set
            showEmail: true,
            showPhone: false,
            phoneIsWhatsApp: false,
            preferredContact: 'email',
        },
        lastUpdated: new Date(),
        // Verification fields
        orderNumber,
        purchaseLocation,
        autoVerified,
        shopifyMatch,
        verificationMethod
    };

    // Check if this product already exists in publicRegistrations
    const publicRef = doc(db, PUB_COL,productData.sku);
    const publicDoc = await getDoc(publicRef);

    if (publicDoc.exists()) {
        throw new Error('This product is already registered and verified.');
    }

    // Defense in depth (2026-06-05): also check whether THIS user already
    // registered this serial. publicRegistrations only catches verified rows
    // (and is never populated on the preview site, where no Cloud Function
    // runs) — without this, an owner could double-register and create
    // duplicates in their quiver.
    const dupQ = query(
        collection(db, REG_COL),
        where('userId', '==', user.uid),
        where('sku', '==', productData.sku)
    );
    const dupSnap = await getDocs(dupQ);
    if (!dupSnap.empty) {
        throw new Error('This board is already registered to your account.');
    }

    const docRef = await addDoc(collection(db, REG_COL), registration);
    return {
        ...registration,
        id: docRef.id
    };
};

// verifyRegistration / unverifyRegistration / syncRegistrationsData removed
// 2026-05-17 (admin retirement Phase 2 cleanup). The old /admin React UI
// at my.waka.surf/admin called these; that subapp has been replaced by
// the helm.waka.surf customer panel. Equivalent server-side write flows
// now live in waka-agent/functions/src/dashboard/registrations-write.ts.
// publicRegistrations writes for auto-verified registrations still
// happen in functions/index.js's sendRegistrationEmail Cloud Function.

// Update registration details (for owners)
export const updateRegistrationDetails = async (id, updateData) => {
    let registrationId = id;

    // Check if this is a public registration ID - if so, find the actual registration
    if (id.startsWith('public-')) {
        const sku = id.replace('public-', '');

        // Query the private registrations to find the actual document
        try {
            const registrationsRef = collection(db, REG_COL);
            const q = query(registrationsRef, where('sku', '==', sku));
            const querySnapshot = await getDocs(q);

            if (!querySnapshot.empty) {
                registrationId = querySnapshot.docs[0].id;
            } else {
                throw new Error('Registration not found');
            }
        } catch (err) {
            throw new Error('Unable to find registration. Please contact support.');
        }
    }

    const registrationRef = doc(db, REG_COL, registrationId);
    const registrationDoc = await getDoc(registrationRef);

    if (!registrationDoc.exists()) {
        throw new Error('Registration not found');
    }
    
    const registrationData = registrationDoc.data();
    
    await updateDoc(registrationRef, {
        ...updateData,
        lastUpdated: new Date()
    });
    
    // Also update the public version if it exists and registration is verified
    if (registrationData.verified) {
        try {
            const publicRef = doc(db, PUB_COL,registrationData.sku);
            const publicDoc = await getDoc(publicRef);
            
            if (publicDoc.exists()) {
                // Update only allowed fields in public copy
                const publicUpdateData = {};
                
                if ('showName' in updateData) {
                    if (updateData.showName) {
                        publicUpdateData.registeredUserName = updateData.customDisplayName || registrationData.registeredUserName;
                    } else {
                        // Remove the name if privacy setting changed
                        publicUpdateData.registeredUserName = null;
                    }
                }
                
                if ('showEmail' in updateData) {
                    if (updateData.showEmail) {
                        publicUpdateData.registeredUserEmail = registrationData.registeredUserEmail;
                    } else {
                        // Remove the email if privacy setting changed
                        publicUpdateData.registeredUserEmail = null;
                    }
                }
                
                if ('customDisplayName' in updateData && registrationData.showName !== false) {
                    publicUpdateData.registeredUserName = updateData.customDisplayName || registrationData.registeredUserName;
                }
                
                if ('phoneNumber' in updateData) {
                    publicUpdateData.phoneNumber = updateData.phoneNumber;
                }
                
                if ('contactMessage' in updateData) {
                    publicUpdateData.contactMessage = updateData.contactMessage;
                }
                
                if (Object.keys(publicUpdateData).length > 0) {
                    publicUpdateData.lastUpdated = new Date();
                    await updateDoc(publicRef, publicUpdateData);
                }
            }
        } catch (error) {
            console.error("Error updating public registration:", error);
            // Continue anyway since the main update succeeded
        }
    }
};

// (unverifyRegistration + syncRegistrationsData removed 2026-05-17 — see
// note above the previous block. Equivalent operator flows live in
// waka-agent/functions/src/dashboard/registrations-write.ts.)